// Company stores (S3, 2026-10-12) — /store/admin/people (store admin).
//
//   The people in the store (name, email, role, joined or invited).
//   Add people: paste emails → POST /portal/store/members → a line per email.
//   Remove someone (asks first) → DELETE /portal/store/members/:id.
//   Resend invite (someone invited who hasn't set a password yet) →
//   POST /portal/store/members/:id/resend (at most every 10 minutes).
//   Join link: "Anyone with an @<domain> email can join" on/off → PATCH
//   settings { joinDomain }, plus a copy button for <portal>/join/<slug>.
// The server picks the store from the admin's membership and refuses
// anything they may not do, in plain words — this page shows those words.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthProvider";
import { PortalLayout } from "@/components/PortalLayout";
import { StoreGate, StoreHeading } from "@/components/StoreGate";
import {
  apiMessage,
  fetchMembers,
  formatDate,
  inviteMembers,
  inviteResultText,
  MY_STORE_KEY,
  removeMember,
  resendInvite,
  updateStoreSettings,
  type InviteResult,
  type MyStore,
  type StoreMember,
} from "@/lib/portal-store";

const MEMBERS_KEY = ["portal-store-members"] as const;
const CARD = "mb-8 rounded-lg border border-rule bg-white p-8 shadow-sm";
const H2 = "mb-1 font-display text-xl font-bold tracking-tight";
const PRIMARY =
  "rounded-md bg-ink px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50";
const SECONDARY =
  "rounded-md border border-rule bg-white px-3 py-1.5 text-sm text-ink transition-colors hover:bg-canvas disabled:opacity-50";

export function StorePeoplePage() {
  return (
    <PortalLayout>
      <StoreGate admin>
        {(my) => (
          <>
            <StoreHeading store={my.store} title="People" />
            <InviteCard />
            <JoinLinkCard my={my} />
            <MembersCard />
          </>
        )}
      </StoreGate>
    </PortalLayout>
  );
}

// ─── Add people ──────────────────────────────────────────────────────────

const GOOD: InviteResult["status"][] = ["invited", "added", "already_member"];

function InviteCard() {
  const qc = useQueryClient();
  const [emails, setEmails] = useState("");
  const [results, setResults] = useState<InviteResult[] | null>(null);
  const invite = useMutation({
    mutationFn: () => inviteMembers(emails),
    onSuccess: (r) => {
      setResults(r);
      // Keep only the ones that didn't go through, so they can be fixed and resent.
      setEmails(r.filter((x) => !GOOD.includes(x.status)).map((x) => x.email).join("\n"));
      void qc.invalidateQueries({ queryKey: MEMBERS_KEY });
    },
  });

  return (
    <section className={CARD}>
      <h2 className={H2}>Add people</h2>
      <p className="mb-4 text-sm text-brown-400">
        Paste their work emails, separated by commas or new lines. We'll email each person a link to set a password.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setResults(null);
          if (emails.trim()) invite.mutate();
        }}
        className="space-y-3"
      >
        <label htmlFor="invite-emails" className="sr-only">
          Emails
        </label>
        <textarea
          id="invite-emails"
          rows={4}
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          placeholder={"sam@yourcompany.com, alex@yourcompany.com"}
          className="w-full max-w-xl rounded-md border border-cream-300 bg-white p-3 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-peach-400 focus:outline-none"
        />
        <div>
          <button type="submit" disabled={invite.isPending || !emails.trim()} className={PRIMARY}>
            {invite.isPending ? "Adding…" : "Add people"}
          </button>
        </div>
      </form>
      {invite.isError && (
        <p role="alert" className="mt-4 text-sm text-rust-500">
          {apiMessage(invite.error)}
        </p>
      )}
      {results && results.length > 0 && (
        <ul className="mt-5 divide-y divide-rule rounded-md border border-rule text-sm">
          {results.map((r) => (
            <li key={r.email} className="flex flex-col gap-0.5 px-4 py-2.5 sm:flex-row sm:gap-4">
              <span className="font-medium text-ink sm:w-64 sm:shrink-0 sm:truncate">{r.email}</span>
              <span className={GOOD.includes(r.status) ? "text-ok" : "text-rust-500"}>{inviteResultText(r)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Join link ───────────────────────────────────────────────────────────

function JoinLinkCard({ my }: { my: MyStore }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const domain = (user?.email.split("@")[1] ?? "").toLowerCase();
  const on = !!my.store.joinDomain;
  const shownDomain = my.store.joinDomain ?? domain;
  const link = `${window.location.origin}/join/${my.store.slug}`;

  const toggle = useMutation({
    mutationFn: (next: boolean) => updateStoreSettings({ joinDomain: next ? domain : null }),
    onSuccess: (next) => qc.setQueryData(MY_STORE_KEY, next),
  });

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", link);
    }
  }

  return (
    <section className={CARD}>
      <h2 className={H2}>Join link</h2>
      <p className="mb-4 text-sm text-brown-400">
        Share one link instead of adding people one by one. Only people with your company email can use it.
      </p>
      <label className="flex cursor-pointer items-center gap-3 text-sm text-ink">
        <button
          type="button"
          role="switch"
          aria-checked={on}
          disabled={toggle.isPending || (!on && !domain)}
          onClick={() => toggle.mutate(!on)}
          className={
            "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 " +
            (on ? "bg-ink" : "bg-cream-400")
          }
        >
          <span
            aria-hidden
            className={"inline-block h-5 w-5 rounded-full bg-white shadow transition-transform " + (on ? "translate-x-5" : "translate-x-0.5")}
          />
        </button>
        <span>Anyone with an @{shownDomain || "your company"} email can join</span>
      </label>
      {toggle.isError && (
        <p role="alert" className="mt-3 text-sm text-rust-500">
          {apiMessage(toggle.error)}
        </p>
      )}
      {on && (
        <div className="mt-5 flex max-w-xl flex-wrap items-center gap-3">
          <code className="min-w-0 flex-1 truncate rounded-md border border-rule bg-canvas px-3 py-2 text-sm text-brown-500">{link}</code>
          <button type="button" onClick={() => void copy()} className={SECONDARY}>
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      )}
    </section>
  );
}

// ─── The people ──────────────────────────────────────────────────────────

function MembersCard() {
  const { user } = useAuth();
  const q = useQuery({ queryKey: MEMBERS_KEY, queryFn: fetchMembers, retry: false });

  return (
    <section className={CARD}>
      <h2 className={`${H2} mb-4`}>Who's in the store</h2>
      {q.isPending ? (
        <p className="text-sm text-brown-400">Loading…</p>
      ) : q.isError ? (
        <p className="text-sm text-rust-500">{apiMessage(q.error)}</p>
      ) : q.data.length === 0 ? (
        <p className="text-sm text-brown-400">Nobody yet. Add people above.</p>
      ) : (
        <div className="relative overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-rule text-xs uppercase tracking-[0.08em] text-muted">
                <th className="py-2 pr-4 font-medium">Name</th>
                <th className="py-2 pr-4 font-medium">Email</th>
                <th className="py-2 pr-4 font-medium">Role</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((m) => (
                <MemberRow key={m.id} member={m} isMe={m.userId === user?.id} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MemberRow({ member, isMe }: { member: StoreMember; isMe: boolean }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => removeMember(member.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MEMBERS_KEY }),
  });
  const resend = useMutation({ mutationFn: () => resendInvite(member.id) });
  const canResend = member.state === "invited" && !isMe;
  const status =
    member.state === "active"
      ? `Joined${member.joinedAt ? ` ${formatDate(member.joinedAt)}` : ""}`
      : `Invited${member.invitedAt ? ` ${formatDate(member.invitedAt)}` : ""}`;

  return (
    <tr className="border-b border-rule last:border-0 align-top">
      <td className="py-3 pr-4 text-ink">
        {member.name}
        {isMe && <span className="text-muted"> (you)</span>}
      </td>
      <td className="py-3 pr-4 text-brown-400">{member.email}</td>
      <td className="py-3 pr-4 text-brown-400">{member.role === "admin" ? "Admin" : "Member"}</td>
      <td className="py-3 pr-4 text-brown-400">{status}</td>
      <td className="py-3 text-right">
        {canResend && !confirming && (
          <button
            type="button"
            disabled={resend.isPending || resend.isSuccess}
            onClick={() => resend.mutate()}
            className={SECONDARY + " mb-1 mr-2"}
          >
            {resend.isPending ? "Sending…" : resend.isSuccess ? "Invite sent" : "Resend invite"}
          </button>
        )}
        {!isMe &&
          (confirming ? (
            <span className="inline-flex flex-wrap items-center justify-end gap-2">
              <span className="text-xs text-brown-400">Remove {member.name}?</span>
              <button type="button" disabled={remove.isPending} onClick={() => remove.mutate()} className={SECONDARY + " text-rust-500"}>
                {remove.isPending ? "Removing…" : "Remove"}
              </button>
              <button type="button" disabled={remove.isPending} onClick={() => setConfirming(false)} className={SECONDARY}>
                Cancel
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirming(true)} className={SECONDARY}>
              Remove
            </button>
          ))}
        {remove.isError && <p className="mt-1 text-xs text-rust-500">{apiMessage(remove.error)}</p>}
        {resend.isError && (
          <p role="alert" className="mt-1 text-xs text-rust-500">
            {apiMessage(resend.error, "We couldn't send the invite. Please try again.")}
          </p>
        )}
        {(resend.isSuccess || resend.isError) && (
          <p className="mt-1 text-xs text-brown-400">They can also use Forgot password on the sign-in page.</p>
        )}
      </td>
    </tr>
  );
}
